import { useEffect, useMemo, useState } from "react";
import MetricCard from "../../components/MetricCard.jsx";
import PlusNudge from "../../components/PlusNudge.jsx";
import { writingGoals } from "../../data/appCatalog.js";
import { useI18n } from "../../i18n.jsx";
import { moduleJsonPath } from "../../lib/legacyPaths.js";
import {
  correctedTypeTokenRatio,
  countUnitVocabulary,
  countWords,
  getSentences,
  getWords,
  meanSentenceLength,
  normalizeWord,
  overusedContentWords
} from "../../lib/textMetrics.js";

const ADVICE_OPTIONS = {
  length: {
    goalKey: "wordGoal",
    title: "Advice on Length",
    metricLabel: "Number of Words",
    chartTitle: "Length of Your Paragraph Versus Benchmarks",
    benchmarkLabel: "Minimum Words",
    valueLabel: "Your Words"
  },
  unitVocab: {
    goalKey: "vocabGoal",
    title: "Advice on Using Unit Vocabulary",
    metricLabel: "Unit Vocabulary Score",
    chartTitle: "Amount of Unit Vocabulary Versus Benchmarks",
    benchmarkLabel: "Minimum Unit Vocabulary Usage",
    valueLabel: "Your Unit Vocabulary Usage"
  },
  vocab: {
    goalKey: "cttrGoal",
    title: "Advice on Vocabulary Variety",
    metricLabel: "Vocabulary Variety Score",
    chartTitle: "Vocabulary Variety of Your Paragraph Versus Benchmarks",
    benchmarkLabel: "Minimum Vocabulary Variety",
    valueLabel: "Your Vocabulary Variety"
  },
  sentence: {
    goalKey: "mslGoal",
    title: "Advice on Sentence Length",
    metricLabel: "Average Sentence Length",
    chartTitle: "Length of Your Paragraph Versus Benchmarks",
    benchmarkLabel: "Minimum Acceptable MLS",
    valueLabel: "Your Average Sentence Length"
  }
};

function uniqueWords(values) {
  const seen = new Set();
  return values
    .map((value) => String(value || "").trim())
    .filter((value) => {
      const key = value.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function getUnitWords(items) {
  return uniqueWords(items.flatMap((item) => [item.cItem, item.mItem, item.wAnswer, item.speaking_target]));
}

function barPercent(value, goal) {
  const maximum = Math.max(Number(value) || 0, Number(goal) || 0, 1);
  return Math.max(2, Math.min(100, Math.round(((Number(value) || 0) / maximum) * 100)));
}

function formatMetric(value, decimals = 0) {
  return decimals ? Number(value || 0).toFixed(decimals) : String(Math.round(Number(value || 0)));
}

function WritingAdviceModal({ advice, onClose }) {
  if (!advice) return null;

  const goalPercent = barPercent(advice.goal, advice.goal);
  const valuePercent = barPercent(advice.value, advice.goal);

  return (
    <div className="writing-advice-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="writing-advice-dialog" role="dialog" aria-modal="true" aria-labelledby="writing-advice-title" onMouseDown={(event) => event.stopPropagation()}>
        <button className="writing-advice-close" type="button" onClick={onClose} aria-label="Close advice">×</button>
        <h2 id="writing-advice-title">{advice.title}</h2>
        <p className="writing-advice-copy">{advice.message}</p>

        <div className="writing-advice-chart" aria-label={advice.chartTitle}>
          <h3>{advice.chartTitle}</h3>
          <div className="writing-advice-legend">
            <span className="benchmark" />
            {advice.metricLabel}
          </div>
          <div className="writing-advice-bars">
            <div className="writing-advice-bar-row">
              <span>{advice.benchmarkLabel}</span>
              <div className="writing-advice-bar-track">
                <strong className="benchmark" style={{ width: `${goalPercent}%` }}>{formatMetric(advice.goal, advice.decimals)}</strong>
              </div>
            </div>
            <div className="writing-advice-bar-row">
              <span>{advice.valueLabel}</span>
              <div className="writing-advice-bar-track">
                <strong className="current" style={{ width: `${valuePercent}%` }}>{formatMetric(advice.value, advice.decimals)}</strong>
              </div>
            </div>
          </div>
        </div>

        {advice.feedback ? <div className="writing-advice-feedback">{advice.feedback}</div> : null}
      </section>
    </div>
  );
}

export default function WritingPage({ level }) {
  const { t } = useI18n();
  const goals = writingGoals[level.id];
  const [moduleIndex, setModuleIndex] = useState(0);
  const [text, setText] = useState("");
  const [items, setItems] = useState([]);
  const [adviceType, setAdviceType] = useState(null);

  useEffect(() => {
    let ignore = false;
    fetch(moduleJsonPath(level.id, moduleIndex + 1))
      .then((response) => {
        if (!response.ok) throw new Error(`Could not load module ${moduleIndex + 1}`);
        return response.json();
      })
      .then((data) => {
        if (!ignore) setItems(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!ignore) setItems([]);
      });
    return () => {
      ignore = true;
    };
  }, [level.id, moduleIndex]);

  const unitWords = useMemo(() => getUnitWords(items), [items]);

  const metrics = useMemo(() => {
    const wordCount = countWords(text);
    const msl = meanSentenceLength(text);
    const cttr = correctedTypeTokenRatio(text);
    const unitVocab = countUnitVocabulary(text, unitWords);
    return {
      wordCount,
      msl,
      cttr,
      unitVocab
    };
  }, [text, unitWords]);

  const wordGoal = goals.wordLimits?.[moduleIndex];
  const vocabGoal = goals.unitWordLimits?.[moduleIndex];
  const mslGoal = goals.meanLengthLimits?.[moduleIndex];
  const cttrGoal = goals.cttrLimits?.[moduleIndex];
  const wordGoalMet = wordGoal ? metrics.wordCount >= wordGoal : false;
  const vocabGoalMet = vocabGoal ? metrics.unitVocab.count >= vocabGoal : false;
  const mslGoalMet = mslGoal ? metrics.msl >= mslGoal : false;
  const cttrGoalMet = cttrGoal ? metrics.cttr >= cttrGoal : false;
  const activeGoalCount = [wordGoal, vocabGoal, mslGoal, cttrGoal].filter(Boolean).length;
  const metGoalCount = [wordGoalMet, vocabGoalMet, mslGoalMet, cttrGoalMet].filter(Boolean).length;
  const primaryGoal = wordGoal || mslGoal || 1;
  const primaryValue = wordGoal ? metrics.wordCount : metrics.msl;
  const progress = Math.min(100, Math.round((primaryValue / primaryGoal) * 100));
  const progressText = wordGoal
    ? wordGoalMet
      ? t("writing.lengthGoalMet")
      : t("writing.wordsToLengthGoal", { count: wordGoal - metrics.wordCount })
    : mslGoalMet
      ? t("writing.sentenceLengthGoalMet")
      : t("writing.wordsToSentenceGoal", { count: Math.max(0, mslGoal - metrics.msl).toFixed(1) });
  const availableAdvice = Object.entries(ADVICE_OPTIONS).filter(([, option]) => {
    if (option.goalKey === "wordGoal") return Boolean(wordGoal);
    if (option.goalKey === "vocabGoal") return Boolean(vocabGoal);
    if (option.goalKey === "cttrGoal") return Boolean(cttrGoal);
    if (option.goalKey === "mslGoal") return Boolean(mslGoal);
    return false;
  });

  const advice = useMemo(() => {
    if (!adviceType) return null;
    const option = ADVICE_OPTIONS[adviceType];
    if (!option) return null;

    if (adviceType === "length") {
      const remaining = Math.max(0, wordGoal - metrics.wordCount);
      return {
        ...option,
        value: metrics.wordCount,
        goal: wordGoal,
        message: wordGoalMet
          ? `You wrote ${metrics.wordCount} words, which meets the goal of ${wordGoal} words. Good job!`
          : `You did not write enough words. You wrote ${metrics.wordCount} words, but you need to write at least ${remaining} more words. Try thinking about what else you can write about. Who? What? Where? When? Why? Provide explanations and details.`
      };
    }

    if (adviceType === "unitVocab") {
      const usedWords = [];
      const unusedWords = [];
      metrics.unitVocab.keycounts.forEach((value, key) => {
        if (value > 0) usedWords.push(key);
        else unusedWords.push(key);
      });
      return {
        ...option,
        value: metrics.unitVocab.count,
        goal: vocabGoal,
        message: vocabGoalMet
          ? `You used vocabulary words from this unit ${metrics.unitVocab.count} times, which meets the goal of ${vocabGoal}. Good job!`
          : `You haven't used enough vocabulary from this unit. Currently, you have used words from this unit ${metrics.unitVocab.count} times, but you should aim for at least ${vocabGoal}. Check the graph and lists of used and unused words below. Try writing more unit vocabulary words, especially the ones you haven't used yet, highlighted in red.`,
        feedback: (
          <div className="writing-word-lists">
            <p>You used the following unit vocabulary words:</p>
            {usedWords.length ? <ul>{usedWords.map((word) => <li key={word}>{word}</li>)}</ul> : <p className="muted-feedback">No unit vocabulary words yet.</p>}
            <p>However, you did NOT yet use the following unit vocabulary words. Try using some of these in your writing:</p>
            <ul className="unused-words">{unusedWords.map((word) => <li key={word}>{word}</li>)}</ul>
          </div>
        )
      };
    }

    if (adviceType === "vocab") {
      const overused = overusedContentWords(text);
      const words = getWords(text);
      return {
        ...option,
        value: metrics.cttr,
        goal: cttrGoal,
        decimals: 2,
        message: cttrGoalMet
          ? `Your vocabulary variety score was ${metrics.cttr.toFixed(2)}, which meets the goal of ${cttrGoal}. Good job!`
          : `You are repeating too much of the same vocabulary. Your vocabulary variety score is ${metrics.cttr.toFixed(2)}, but you should aim for at least ${cttrGoal}. Check the graph and highlighted writing below. Try writing more words and replacing the red words with synonyms or writing to remove some instances of them.`,
        feedback: words.length ? (
          <p className="writing-highlighted-text">
            {words.map((word, index) => {
              const normalized = normalizeWord(word);
              const highlighted = overused.has(normalized);
              return (
                <span key={`${word}-${index}`} className={highlighted ? "is-overused" : undefined}>
                  {highlighted ? <a href={`https://www.thesaurus.com/browse/${encodeURIComponent(normalized)}`} target="_blank" rel="noreferrer">{word}</a> : word}
                  {" "}
                </span>
              );
            })}
          </p>
        ) : null
      };
    }

    if (adviceType === "sentence") {
      const sentences = getSentences(text);
      return {
        ...option,
        value: metrics.msl,
        goal: mslGoal,
        decimals: 2,
        message: mslGoalMet
          ? `You wrote an average of ${metrics.msl.toFixed(2)} words per sentence, which meets the goal of ${mslGoal}. Good job!`
          : `Your sentences are too short. Your sentences have an average of ${metrics.msl.toFixed(2)} words per sentence, but you should aim for at least ${mslGoal}. Check the graph and try fixing the sentences highlighted in blue below. Use relative clauses, more descriptive words, and combine sentences with conjunctions (e.g., and, but, because) where you can.`,
        feedback: sentences.length ? (
          <p className="writing-highlighted-text">
            {sentences.map((sentence, index) => {
              const highlighted = countWords(sentence) < mslGoal;
              return <span key={`${sentence}-${index}`} className={highlighted ? "is-short-sentence" : undefined}>{sentence} </span>;
            })}
          </p>
        ) : null
      };
    }

    return null;
  }, [adviceType, cttrGoal, cttrGoalMet, metrics, mslGoal, mslGoalMet, text, vocabGoal, vocabGoalMet, wordGoal, wordGoalMet]);

  return (
    <section className="writing-view writing-workspace">
      <div className="setup-panel compact-panel writing-setup-card">
        <label className="field-label" htmlFor="writing-module">{t("writing.currentModule")}</label>
        <select id="writing-module" value={moduleIndex} onChange={(event) => setModuleIndex(Number(event.target.value))}>
          {Array.from({ length: level.modules }, (_, index) => (
            <option key={index} value={index}>{t("common.module", { number: index + 1 })}</option>
          ))}
        </select>
      </div>

      <div className="directions-card full-span writing-brief">
        <div>
          <p className="kicker">{t("writing.kicker", { level: t(level.nameKey, {}, level.label) })}</p>
          <h2>{t("writing.prompt")}</h2>
          <p>{t("writing.currentModuleValue", { number: moduleIndex + 1 })}</p>
        </div>
        <div className="writing-brief-stats" aria-label={t("writing.metricsLabel")}>
          <span>
            <strong>{metrics.wordCount}</strong>
            {t("common.words")}
          </span>
          <span>
            <strong>{metrics.msl.toFixed(1)}</strong>
            {t("writing.sentenceLength")}
          </span>
          <span>
            <strong>{activeGoalCount ? `${metGoalCount}/${activeGoalCount}` : "-"}</strong>
            {t("writing.goalsMet")}
          </span>
        </div>
      </div>

      <div className="editor-card writing-editor-card">
        <div className="writing-card-header">
          <div>
            <p className="kicker">{t("writing.yourDraft")}</p>
            <h2>{t("writing.workspace")}</h2>
          </div>
          <span>{t("common.module", { number: moduleIndex + 1 })}</span>
        </div>
        <textarea value={text} onChange={(event) => setText(event.target.value)} placeholder={t("writing.placeholder")} />
        <div className="writing-progress" aria-label={progressText}>
          <span style={{ width: `${progress}%` }} />
        </div>
        <p className={wordGoalMet || (!wordGoal && mslGoalMet) ? "writing-progress-label is-good" : "writing-progress-label"}>
          {progressText}
        </p>
      </div>

      <aside className="goals-card writing-goals-card">
        <div className="writing-card-header">
          <div>
            <p className="kicker">{t("writing.liveGoals")}</p>
            <h2>{t("writing.goals")}</h2>
          </div>
        </div>
        <div className="writing-advice-panel">
          <p>{t("writing.advicePrompt")}</p>
          {availableAdvice.map(([id]) => (
            <button key={id} type="button" className="writing-advice-button" onClick={() => setAdviceType(id)}>
              {id === "length" ? t("writing.length") : id === "unitVocab" ? t("writing.unitVocabulary") : id === "vocab" ? t("writing.wordVariety") : t("writing.sentenceLength")}
            </button>
          ))}
        </div>
        <MetricCard label={t("writing.length")} value={metrics.wordCount} detail={wordGoal ? t("writing.goalWords", { count: wordGoal }) : t("writing.goldQualityGoals")} status={wordGoal ? (wordGoalMet ? "good" : "needs-work") : "neutral"} />
        <MetricCard label={t("writing.unitVocabulary")} value={metrics.unitVocab.count} detail={vocabGoal ? t("writing.goalUnitWords", { count: vocabGoal }) : t("writing.goalDataPending")} status={vocabGoal ? (vocabGoalMet ? "good" : "needs-work") : "neutral"} />
        <MetricCard label={t("writing.sentenceLength")} value={metrics.msl.toFixed(1)} detail={mslGoal ? t("writing.goalNumber", { count: mslGoal }) : t("writing.goalDataPending")} status={mslGoal && metrics.msl >= mslGoal ? "good" : "needs-work"} />
        {cttrGoal ? <MetricCard label={t("writing.wordVariety")} value={metrics.cttr.toFixed(2)} detail={t("writing.goalCttr", { count: cttrGoal })} status={cttrGoalMet ? "good" : "needs-work"} /> : null}
        <PlusNudge className="workspace-plus-nudge" source="writing-adfree-hint">
          {t("writing.adfreeHint")}
        </PlusNudge>
      </aside>
      <WritingAdviceModal advice={advice} onClose={() => setAdviceType(null)} />
    </section>
  );
}
